/**
 * Thin HTTP client for /api/v1/Conversation/*
 */
import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';
import type {
  AddAttachmentRequest,
  AddParticipantRequest,
  ConversationAttachmentDto,
  ConversationDto,
  ConversationListFilterParams,
  ConversationListParams,
  ConversationListResponse,
  ConversationMessageDto,
  ConversationMessagesPageDto,
  ConversationMessagesParams,
  ConversationParticipantDto,
  ConversationSearchUsersParams,
  ConversationSearchUsersResponse,
  DeleteConversationRequest,
  DeleteMessageRequest,
  InitiateConversationRequest,
  RemoveParticipantRequest,
  SendMessageRequest,
  UnreadCountResponse,
  UpdateMessageRequest,
} from './conversationTypes';
import {
  buildInitiateConversationPayload,
  type BuildInitiatePayloadOptions,
} from './initiateConversationPayload';

const conversationApi = {
  initiate: (
    payload: InitiateConversationRequest,
    options?: BuildInitiatePayloadOptions,
  ): Promise<AxiosResponse<ConversationDto>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.INITIATE,
      buildInitiateConversationPayload(payload, options),
    ),

  list: (
    params: ConversationListParams = {},
  ): Promise<AxiosResponse<ConversationListResponse>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.LIST, {
      params: {
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    }),

  listWithFilter: (
    params: ConversationListFilterParams = {},
  ): Promise<AxiosResponse<ConversationListResponse>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.LIST_WITH_FILTER, {
      params: {
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20,
        ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
        filter: params.filter ?? 'All',
      },
    }),

  getById: (
    conversationId: number | string,
  ): Promise<AxiosResponse<ConversationDto>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.BY_ID(conversationId)),

  delete: (
    payload: DeleteConversationRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(API_ENDPOINTS.CONVERSATION.DELETE, payload),

  searchUsers: (
    params: ConversationSearchUsersParams = {},
  ): Promise<AxiosResponse<ConversationSearchUsersResponse>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.SEARCH_USERS, {
      params: {
        searchQuery: params.searchQuery ?? '',
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 10,
        onlineOnly: params.onlineOnly ?? false,
        excludeConversationParticipants:
          params.excludeConversationParticipants ?? false,
      },
    }),

  sendMessage: (
    conversationId: number | string,
    payload: SendMessageRequest,
  ): Promise<AxiosResponse<ConversationMessageDto>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.SEND_MESSAGE(conversationId),
      payload,
    ),

  updateMessage: (
    conversationId: number | string,
    payload: UpdateMessageRequest,
  ): Promise<AxiosResponse<ConversationMessageDto>> =>
    apiClient.put(
      API_ENDPOINTS.CONVERSATION.UPDATE_MESSAGE(conversationId),
      payload,
    ),

  deleteMessage: (
    conversationId: number | string,
    payload: DeleteMessageRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.DELETE_MESSAGE(conversationId),
      payload,
    ),

  getMessages: (
    conversationId: number | string,
    params: ConversationMessagesParams = {},
  ): Promise<AxiosResponse<ConversationMessagesPageDto>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.MESSAGES(conversationId), {
      params: {
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20,
        sortDescending: params.sortDescending ?? true,
        timezoneOffset: params.timezoneOffset,
      },
    }),

  markMessageRead: (
    conversationId: number | string,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.MARK_MESSAGE_READ(conversationId),
    ),

  getUnreadCount: (
    conversationId: number | string,
  ): Promise<AxiosResponse<UnreadCountResponse>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.UNREAD_COUNT(conversationId)),

  addAttachment: (
    conversationId: number | string,
    messageId: number | string,
    payload: AddAttachmentRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.ADD_ATTACHMENT(conversationId, messageId),
      payload,
    ),

  getAttachments: (
    conversationId: number | string,
    messageId: number | string,
  ): Promise<AxiosResponse<ConversationAttachmentDto[]>> =>
    apiClient.get(
      API_ENDPOINTS.CONVERSATION.ATTACHMENTS(conversationId, messageId),
    ),

  addParticipant: (
    conversationId: number | string,
    payload: AddParticipantRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.ADD_PARTICIPANT(conversationId),
      payload,
    ),

  removeParticipant: (
    conversationId: number | string,
    payload: RemoveParticipantRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(
      API_ENDPOINTS.CONVERSATION.REMOVE_PARTICIPANT(conversationId),
      payload,
    ),

  getParticipants: (
    conversationId: number | string,
  ): Promise<AxiosResponse<ConversationParticipantDto[]>> =>
    apiClient.get(API_ENDPOINTS.CONVERSATION.PARTICIPANTS(conversationId)),
};

export default conversationApi;
